import { useEffect, type ReactNode } from "react";
import { LivePreviewLink } from "../components/LivePreviewLink";
import { Newsletter } from "../components/Newsletter";
import { StepsTracker } from "../components/StepsTracker";

type PortfolioRow = {
  year?: string;
  title: string;
  href?: string;
  detail: string;
};

const products: PortfolioRow[] = [
  {
    year: "2026–now",
    title: "Tivrik",
    detail: "Value-investing terminal · Financials and valuation tools",
  },
  {
    year: "2024–now",
    title: "Shippeek",
    href: "https://shippeek.com",
    detail: "Founder + Builder · Multi-carrier shipping platform",
  },
  {
    year: "2025",
    title: "Dimension",
    href: "https://dimension.fyi",
    detail: "Shopify app · Bulk product dimensions",
  },
  {
    year: "2025",
    title: "BrollHQ",
    href: "https://brollhq.com",
    detail: "AI-assisted video editor · Discontinued",
  },
];

const experiments: PortfolioRow[] = [
  {
    year: "2025",
    title: "Paper shader",
    href: "/components/paper",
    detail: "Shader and interaction study",
  },
  {
    year: "2025",
    title: "Carousel minimap",
    href: "/components/image-coursel-minimap",
    detail: "Navigation experiment",
  },
  {
    year: "2025",
    title: "Multi-level menu",
    href: "/components/multimenu",
    detail: "Responsive navigation study",
  },
];

const experience: PortfolioRow[] = [
  {
    year: "2025–now",
    title: "Confidential e-commerce platform",
    detail: "Forward Deployed Engineer",
  },
  {
    year: "2025",
    title: "Veza Digital",
    href: "https://www.vezadigital.com/",
    detail: "Product Engineer",
  },
  {
    year: "2024",
    title: "BMG Studio",
    detail: "Product Engineer",
  },
  {
    year: "2024",
    title: "Ad Conversion",
    href: "https://www.adconversion.com/",
    detail: "Product + Engineering Consultant",
  },
  {
    year: "2020–now",
    title: "Freelance",
    detail: "Product, design, and engineering",
  },
];

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-14 sm:mt-16 first:mt-0">
      <h2
        className="mb-5 text-sm font-medium"
        style={{ color: "rgb(var(--text-primary))" }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function PortfolioList({ items }: { items: PortfolioRow[] }) {
  return (
    <ul className="space-y-5">
      {items.map(item => {
        const isExternal = item.href?.startsWith("http");
        const linkClassName = "inline-flex min-h-6 items-center font-medium underline-offset-4 hover:underline";
        const linkStyle = { color: "rgb(var(--text-primary))" };
        const title = item.href && isExternal ? (
          <>
            <LivePreviewLink
              href={item.href}
              label={item.title}
              anchorClassName={linkClassName}
              anchorStyle={linkStyle}
            />{" "}
            <span aria-hidden="true" className="home-row-arrow">↗</span>
          </>
        ) : item.href ? (
          <a
            href={item.href}
            target={isExternal ? "_blank" : undefined}
            rel={isExternal ? "noopener noreferrer" : undefined}
            className={linkClassName}
            style={linkStyle}
          >
            {item.title} <span aria-hidden="true" className="home-row-arrow ml-1.5">{isExternal ? "↗" : "→"}</span>
          </a>
        ) : (
          <span className="font-medium" style={{ color: "rgb(var(--text-primary))" }}>
            {item.title}
          </span>
        );

        return (
          <li key={`${item.year}-${item.title}`} className="home-row grid grid-cols-[76px_minmax(0,1fr)] items-baseline gap-x-4 sm:grid-cols-[88px_minmax(0,1fr)] sm:gap-x-5">
            <span className="text-xs tabular-nums" style={{ color: "rgb(var(--text-tertiary))" }}>
              {item.year}
            </span>
            <div className="min-w-0">
              <div className="text-sm leading-6">{title}</div>
              <p className="mt-1 text-[13px] leading-relaxed text-pretty" style={{ color: "rgb(var(--text-secondary))" }}>
                {item.detail}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function HomePage() {
  useEffect(() => {
    document.title = "Yash Bindal | Forward Deployed Engineer";

    let descriptionTag = document.querySelector('meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      document.head.appendChild(descriptionTag);
    }

    descriptionTag.setAttribute(
      "content",
      "Yash Bindal is a Forward Deployed Engineer working in e-commerce across product design, front-end, and full-stack systems.",
    );
  }, []);

  return (
    <main id="top" className="home-page min-h-screen w-full px-6 py-20 sm:py-28">
      <div className="mx-auto max-w-[var(--container-width)]">
        <header className="flex flex-col gap-7 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1
              className="text-lg font-medium"
              style={{ color: "rgb(var(--text-primary))" }}
            >
              Yash Bindal
            </h1>
            <p className="mt-1 text-sm" style={{ color: "rgb(var(--text-secondary))" }}>
              Forward Deployed Engineer
            </p>
          </div>

          <nav aria-label="Primary navigation" className="home-nav flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
            <a href="/" aria-current="page" className="font-medium underline underline-offset-4" style={{ color: "rgb(var(--text-primary))" }}>
              Home
            </a>
            <a href="/cv" className="hover:underline hover:underline-offset-4" style={{ color: "rgb(var(--text-secondary))" }}>
              CV
            </a>
            <a href="/tools" className="hover:underline hover:underline-offset-4" style={{ color: "rgb(var(--text-secondary))" }}>
              Tools
            </a>
            <a href="mailto:itisyb@gmail.com" className="hover:underline hover:underline-offset-4" style={{ color: "rgb(var(--text-secondary))" }}>
              Get in touch
            </a>
          </nav>
        </header>

        <div className="mt-12 max-w-[610px] space-y-4 text-[15px] leading-[1.7]" style={{ color: "rgb(var(--text-secondary))" }}>
          <p>
            Hello, I’m Yash. I’m a Forward Deployed Engineer currently working in e-commerce.
          </p>
          <p>
            For the past six years, I’ve worked across product design and full-stack engineering, from dashboards and polished interfaces to APIs, PostgreSQL, integrations, and internal operational tools.
          </p>
          <p>
            At work, I help build and maintain a custom e-commerce platform and its ERP. Outside work, I’m building{" "}
            <LivePreviewLink
              href="https://shippeek.com"
              label="Shippeek"
              anchorClassName="font-medium underline decoration-dashed underline-offset-2 transition-colors"
              anchorStyle={{ color: "rgb(var(--text-primary))" }}
              domainLabel="shippeek.com"
            />
            {" "}and <span className="font-medium" style={{ color: "rgb(var(--text-primary))" }}>Tivrik</span> as side projects, while exploring motion and interaction design.
          </p>
        </div>

        <StepsTracker />

        <Section title="Selected products">
          <PortfolioList items={products} />
        </Section>

        <Section title="Experience">
          <PortfolioList items={experience} />
        </Section>

        <Section title="Experiments">
          <PortfolioList items={experiments} />
        </Section>

        <Section title="Elsewhere">
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
            <a href="https://x.com/yashbindal__" target="_blank" rel="noopener noreferrer" className="font-medium underline-offset-4 hover:underline" style={{ color: "rgb(var(--text-primary))" }}>
              X / Twitter ↗
            </a>
            <a href="https://github.com/itisyb" target="_blank" rel="noopener noreferrer" className="font-medium underline-offset-4 hover:underline" style={{ color: "rgb(var(--text-primary))" }}>
              GitHub ↗
            </a>
            <a href="https://contra.com/yashbindal" target="_blank" rel="noopener noreferrer" className="font-medium underline-offset-4 hover:underline" style={{ color: "rgb(var(--text-primary))" }}>
              Contra ↗
            </a>
          </div>
        </Section>

        <Section title="Newsletter">
          <p className="mb-4 text-sm" style={{ color: "rgb(var(--text-secondary))" }}>
            Occasional notes about products, engineering, and things I’m learning.
          </p>
          <Newsletter />
        </Section>

        <footer className="mt-16 flex items-center justify-between gap-6 pb-4 text-xs sm:mt-20" style={{ color: "rgb(var(--text-tertiary))" }}>
          <span>© {new Date().getFullYear()} Yash Bindal</span>
          <a href="#top" className="py-3 underline-offset-4 hover:underline">Back to top <span aria-hidden="true">↑</span></a>
        </footer>
      </div>
    </main>
  );
}
